import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageSquarePlus, Send } from "lucide-react";
import { api, getAxiosErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import FileUpload, { FileList } from "@/components/FileUpload";
import { toast } from "sonner";

/**
 * Édition d'une action corrective existante.
 *
 * L'écran ne proposait que quatre champs en édition en ligne, alors que le
 * backend en accepte douze. Consigner ce qui avait été fait obligeait donc à
 * réécrire le champ « Action » — donc à écraser ce qui était prévu par ce qui a
 * été réalisé, et à perdre l'écart entre les deux, que l'audit ISO vient
 * précisément regarder.
 *
 * D'où la séparation, visible ici : ce qui était PRÉVU d'un côté, ce qui a été
 * RÉALISÉ de l'autre, et le suivi en commentaires datés qui n'écrasent rien.
 *
 * `null` est envoyé explicitement pour vider un champ : le backend distingue
 * « ne touche pas » (clé absente) de « efface » (`null`).
 */
const AUCUN = "none";

export default function CorrectiveActionEditor({ action, users, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    cause: action.cause || "",
    action: action.action || "",
    responsible_id: action.responsible_id || AUCUN,
    due_date: (action.due_date || "").slice(0, 10),
    status: action.status || "ouverte",
    resolution_type: action.resolution_type || "ponctuelle",
    verification_date: (action.verification_date || "").slice(0, 10),
    verification_status: action.verification_status || AUCUN,
    result: action.result || "",
    completed_at: (action.completed_at || "").slice(0, 10),
    effectiveness_notes: action.effectiveness_notes || "",
  }));
  const [enregistrement, setEnregistrement] = useState(false);
  const [commentaires, setCommentaires] = useState([]);
  const [nouveauCommentaire, setNouveauCommentaire] = useState("");
  const [historique, setHistorique] = useState([]);
  const [fichiers, setFichiers] = useState([]);

  const set = (cle, valeur) => setForm((f) => ({ ...f, [cle]: valeur }));

  const recharger = useCallback(async () => {
    const [c, h, f] = await Promise.all([
      api.get(`/corrective-actions/${action.id}/comments`).catch(() => ({ data: [] })),
      api.get(`/corrective-actions/${action.id}/audit`).catch(() => ({ data: [] })),
      api.get(`/files/entity/corrective_action/${action.id}`).catch(() => ({ data: [] })),
    ]);
    setCommentaires(c.data || []);
    setHistorique(h.data || []);
    setFichiers(f.data || []);
  }, [action.id]);

  useEffect(() => { recharger(); }, [recharger]);

  const enregistrer = async (e) => {
    e.preventDefault();
    setEnregistrement(true);
    try {
      // `null` et non chaîne vide : le backend traite `null` comme une
      // intention d'effacer, une chaîne vide resterait une valeur.
      const charge = {
        cause: form.cause,
        action: form.action,
        responsible_id: form.responsible_id === AUCUN ? null : form.responsible_id,
        due_date: form.due_date,
        status: form.status,
        resolution_type: form.resolution_type,
        verification_date: form.verification_date || null,
        verification_status:
          form.verification_status === AUCUN ? null : form.verification_status,
        result: form.result,
        completed_at: form.completed_at || null,
        effectiveness_notes: form.effectiveness_notes,
      };
      const { data } = await api.patch(`/corrective-actions/${action.id}`, charge);
      toast.success(`Action ${data.number} enregistrée`);
      onSaved?.(data);
    } catch (err) {
      toast.error(getAxiosErrorMessage(err));
    } finally {
      setEnregistrement(false);
    }
  };

  const commenter = async () => {
    const contenu = nouveauCommentaire.trim();
    if (!contenu) return;
    try {
      await api.post(`/corrective-actions/${action.id}/comments`, { content: contenu });
      setNouveauCommentaire("");
      recharger();
    } catch (err) {
      toast.error(getAxiosErrorMessage(err));
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="action-editor">
      <DialogHeader>
        <DialogTitle className="font-heading">{action.number}</DialogTitle>
        <DialogDescription>
          {action.complaint_number
            ? `Litige associé : ${action.complaint_number}`
            : "Action corrective non rattachée à un litige"}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={enregistrer} className="space-y-5">
        {/* ---------- Ce qui était prévu ---------- */}
        <fieldset className="space-y-3">
          <legend className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
            Ce qui était prévu
          </legend>
          <div>
            <Label htmlFor="ac-cause">Cause</Label>
            <Textarea
              id="ac-cause" rows={2} value={form.cause}
              onChange={(e) => set("cause", e.target.value)}
              className="rounded-xl" data-testid="action-cause"
            />
          </div>
          <div>
            <Label htmlFor="ac-action">Action</Label>
            <Textarea
              id="ac-action" rows={2} value={form.action}
              onChange={(e) => set("action", e.target.value)}
              className="rounded-xl" data-testid="action-action"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Responsable</Label>
              <Select
                value={form.responsible_id}
                onValueChange={(v) => set("responsible_id", v)}
              >
                <SelectTrigger className="rounded-xl" data-testid="action-responsible">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Retirer un responsable doit être possible : le backend
                      traite `null` comme une intention explicite. */}
                  <SelectItem value={AUCUN}>Aucun responsable</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ac-due">Échéance</Label>
              <Input
                id="ac-due" type="date" value={form.due_date}
                onChange={(e) => set("due_date", e.target.value)}
                className="rounded-xl" data-testid="action-due-date"
              />
            </div>
          </div>
        </fieldset>

        {/* ---------- Ce qui a été fait ---------- */}
        <fieldset className="space-y-3 border-t border-slate-100 pt-4">
          <legend className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
            Ce qui a été fait
          </legend>
          <div>
            <Label htmlFor="ac-result">Résultat</Label>
            <Textarea
              id="ac-result" rows={2} value={form.result}
              onChange={(e) => set("result", e.target.value)}
              placeholder="Ce qui a réellement été mis en place"
              className="rounded-xl" data-testid="action-result"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="ac-completed">Date de réalisation</Label>
              <Input
                id="ac-completed" type="date" value={form.completed_at}
                onChange={(e) => set("completed_at", e.target.value)}
                className="rounded-xl" data-testid="action-completed-at"
              />
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="rounded-xl" data-testid="action-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Quatre statuts, propres aux actions correctives : ils ne
                      suivent pas la simplification faite sur les litiges. */}
                  <SelectItem value="ouverte">Ouverte</SelectItem>
                  <SelectItem value="en_cours">En cours</SelectItem>
                  <SelectItem value="cloturee">Clôturée</SelectItem>
                  <SelectItem value="inefficace">Inefficace</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </fieldset>

        {/* ---------- Vérification d'efficacité ---------- */}
        <fieldset className="space-y-3 border-t border-slate-100 pt-4">
          <legend className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
            Vérification d'efficacité
          </legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>Type de résolution</Label>
              <Select
                value={form.resolution_type}
                onValueChange={(v) => set("resolution_type", v)}
              >
                <SelectTrigger className="rounded-xl" data-testid="action-resolution-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ponctuelle">Ponctuelle</SelectItem>
                  <SelectItem value="permanente">Permanente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ac-verif-date">Date de vérification</Label>
              <Input
                id="ac-verif-date" type="date" value={form.verification_date}
                onChange={(e) => set("verification_date", e.target.value)}
                className="rounded-xl" data-testid="action-verification-date"
              />
            </div>
            <div>
              <Label>Résultat de vérification</Label>
              <Select
                value={form.verification_status}
                onValueChange={(v) => set("verification_status", v)}
              >
                <SelectTrigger className="rounded-xl" data-testid="action-verification-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUCUN}>Non applicable</SelectItem>
                  <SelectItem value="a_verifier">À vérifier</SelectItem>
                  <SelectItem value="efficace">Efficace</SelectItem>
                  {/* Axe distinct du statut : une action peut être clôturée et
                      sa vérification conclure à l'inefficacité. */}
                  <SelectItem value="inefficace">Inefficace</SelectItem>
                  <SelectItem value="a_reouvrir">À rouvrir</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="ac-notes">Notes de vérification</Label>
            <Textarea
              id="ac-notes" rows={2} value={form.effectiveness_notes}
              onChange={(e) => set("effectiveness_notes", e.target.value)}
              className="rounded-xl" data-testid="action-effectiveness-notes"
            />
          </div>
        </fieldset>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
            Fermer
          </Button>
          <Button
            type="submit" disabled={enregistrement}
            className="rounded-xl bg-blue-600 text-white hover:bg-blue-700"
            data-testid="action-save"
          >
            {enregistrement ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Enregistrer
          </Button>
        </DialogFooter>
      </form>

      {/* ---------- Suivi ---------- */}
      <section className="border-t border-slate-100 pt-4">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
          Suivi
        </h3>
        {commentaires.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="action-no-comment">
            Aucun commentaire.
          </p>
        ) : (
          <ul className="mb-3 space-y-2" data-testid="action-comments">
            {commentaires.map((c) => (
              <li key={c.id} className="rounded-xl bg-slate-50 px-3 py-2">
                <div className="text-xs text-slate-500">
                  {c.author_name} · {(c.created_at || "").slice(0, 10)}
                </div>
                <div className="whitespace-pre-wrap text-sm text-slate-800">{c.content}</div>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <Input
            value={nouveauCommentaire}
            onChange={(e) => setNouveauCommentaire(e.target.value)}
            placeholder="Point d'étape, obstacle, décision…"
            className="rounded-xl"
            data-testid="action-comment-input"
          />
          <Button
            type="button" onClick={commenter}
            className="rounded-xl bg-blue-600 text-white hover:bg-blue-700"
            data-testid="action-comment-submit"
          >
            <Send size={14} />
          </Button>
        </div>
      </section>

      {/* ---------- Pièces jointes ---------- */}
      <section className="border-t border-slate-100 pt-4">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
          Pièces jointes
        </h3>
        <FileUpload
          entityType="corrective_action" entityId={action.id}
          onUploaded={recharger}
        />
        <div className="mt-3">
          <FileList files={fichiers} />
        </div>
      </section>

      {/* ---------- Historique ---------- */}
      <section className="border-t border-slate-100 pt-4">
        <h3 className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
          <MessageSquarePlus size={13} strokeWidth={1.75} />
          Historique
        </h3>
        {historique.length === 0 ? (
          <p className="text-sm text-slate-400">Aucun événement.</p>
        ) : (
          <ul className="space-y-1.5" data-testid="action-history">
            {historique.slice().reverse().map((e) => (
              <li key={e.id} className="text-xs text-slate-600">
                <span className="text-slate-400">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
                {" · "}
                <span className="font-medium text-slate-800">{e.user_name}</span>
                {" · "}
                {e.description}
              </li>
            ))}
          </ul>
        )}
      </section>
    </DialogContent>
  );
}
